/**
 * fapiBase.js — single source of truth for the Binance REST base URL.
 *
 * Why this matters for scale & anti-ban:
 *   • DEV  → '/binance-fapi' / '/binance-spot' (Vite dev proxy, avoids CORS).
 *            ⚠ In dev ALL public requests share the dev machine's single IP, so
 *            keep request volume low while developing.
 *   • PROD → set these env vars at build time:
 *              VITE_FAPI_BASE=https://fapi.binance.com
 *              VITE_SPOT_BASE=https://api.binance.com
 *            Then every user's browser calls Binance DIRECTLY from THEIR OWN IP.
 *            REST weight is spread across all user IPs instead of funnelling
 *            through one shared server IP → a single shared IP can never be
 *            rate-limited/banned for the whole user base.
 *
 * Binance public market-data endpoints (klines, ticker, exchangeInfo, depth)
 * send permissive CORS headers, so direct browser calls work without a proxy.
 */
export const FAPI_BASE = import.meta.env.VITE_FAPI_BASE || '/binance-fapi';
export const SPOT_BASE = import.meta.env.VITE_SPOT_BASE || '/binance-spot';

// A tradable Binance USDT-perp symbol (e.g. BTCUSDT). Used to skip klines
// requests for malformed / non-Latin symbols that would 400.
export const isValidSymbol = (s) => typeof s === 'string' && /^[A-Z0-9]{1,20}USDT$/.test(s);

/**
 * Server-side klines cache proxy (Node backend, behind nginx at /api/market).
 *
 * When VITE_KLINES_PROXY is set (prod: "/api/market"), all candle requests go to
 * OUR server, which serves them from a weight-aware shared cache fetched once
 * from the server IP — instead of every browser hammering Binance. The browser
 * gets one fast response; Binance sees one IP, not thousands.
 *
 * When unset (dev), we fall back to FAPI_BASE (Binance / Vite proxy) so local
 * development needs no backend running.
 */
export const KLINES_PROXY = import.meta.env.VITE_KLINES_PROXY || '';

/**
 * Build a futures klines URL. Uses the server proxy when configured, else the
 * direct Binance futures endpoint.
 * @param {{symbol:string, interval:string, limit?:number, endTime?:number|null}} p
 */
export function klinesUrl({ symbol, interval, limit = 500, endTime = null }) {
  if (KLINES_PROXY) {
    let u = `${KLINES_PROXY}/klines?symbol=${symbol}&interval=${interval}&limit=${limit}`;
    if (endTime) u += `&endTime=${endTime}`;
    return u;
  }
  let u = `${FAPI_BASE}/fapi/v1/klines?symbol=${symbol}&interval=${interval}&limit=${limit}`;
  if (endTime) u += `&endTime=${endTime}`;
  return u;
}

/**
 * Build a Binance SPOT klines URL (direct — no futures proxy). Returns null when
 * the symbol clearly has no spot market convention. Used for spot-volume growth.
 * @param {{symbol:string, interval:string, limit?:number}} p
 */
export function spotKlinesUrl({ symbol, interval, limit = 500 }) {
  return `${SPOT_BASE}/api/v3/klines?symbol=${symbol}&interval=${interval}&limit=${limit}`;
}

/**
 * Static CDN base for pre-built kline snapshots (prod: "https://cdn.example.com"
 * or a same-origin path like "/cdn"). The Go ingestor publishes
 * <CDN>/ohlc/<SYMBOL>_<interval>.json — Binance's array-of-arrays shape verbatim,
 * edge-cached by Cloudflare. The browser loads chart HISTORY from here (instant,
 * no Binance, no our server); the live candle still streams tick-by-tick over the
 * Binance kline WS. When unset, the chart falls back to klinesUrl() unchanged.
 */
export const CDN_BASE = import.meta.env.VITE_CDN_BASE || '';

/**
 * URL of a pre-built kline snapshot on the CDN, or null when no CDN is configured
 * (caller then uses klinesUrl()). The CDN only carries a fixed-depth snapshot per
 * symbol/interval — no endTime paging; deeper history falls back to klinesUrl().
 * @param {{symbol:string, interval:string}} p
 */
export function cdnKlinesUrl({ symbol, interval }) {
  if (!CDN_BASE) return null;
  return `${CDN_BASE}/ohlc/${symbol}_${interval}.json`;
}

// Interval → milliseconds. Used to gate CDN freshness (skip a stale snapshot and
// fall back to the live proxy) and elsewhere.
const INTERVAL_MS = {
  '1m': 60e3, '3m': 180e3, '5m': 300e3, '15m': 900e3, '30m': 1.8e6,
  '1h': 3.6e6, '2h': 7.2e6, '4h': 1.44e7, '6h': 2.16e7, '8h': 2.88e7,
  '12h': 4.32e7, '1d': 8.64e7, '3d': 2.592e8, '1w': 6.048e8,
};
export const intervalMs = (interval) => INTERVAL_MS[interval] || 60e3;

/**
 * How stale a CDN snapshot may be before we ignore it and load the live tail from
 * the proxy/Binance instead. Two candles of slack, with a 90s floor so a healthy
 * exporter (refreshes 1m every ~20s) always passes while a dead one degrades to
 * the existing path.
 */
export const cdnStaleMs = (interval) => Math.max(intervalMs(interval) * 2, 90e3);

/**
 * Last-resort DIRECT Binance klines URL (browser → Binance, the user's own IP).
 * Used as a fallback when our proxy is throttled/down (503/429) so a chart ALWAYS
 * renders. Binance sends permissive CORS on market-data endpoints. Not cached in
 * our DB (it bypasses the server), but it keeps the chart alive.
 */
export function binanceDirectKlinesUrl({ symbol, interval, limit = 500, endTime = null }) {
  let u = `https://fapi.binance.com/fapi/v1/klines?symbol=${symbol}&interval=${interval}&limit=${limit}`;
  if (endTime) u += `&endTime=${endTime}`;
  return u;
}
